// Library Imports
import React, { forwardRef, useState } from 'react';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import { TextField } from '@mui/material';
import { IMaskInput } from 'react-imask';

// Local Imports
import { VisibilityIcon } from '@/assets/icons/VisibilityIcon';
import { VisibilityOffIcon } from '@/assets/icons/VisibilityOffIcon';

const TextMask = forwardRef(({ onChange, ...other }, ref) => {
  return (
    <IMaskInput
      {...other}
      mask={other?.maskformat || '#00-000-0000'}
      definitions={{ '#': /[1-9]/ }}
      inputRef={ref}
      onAccept={value => onChange({ target: { name: other.name, value } })}
      overwrite
    />
  );
});

export const TextInput = ({
  name,
  value,
  onChange,
  disabled,
  type,
  size = 'medium',
  label = '',
  error,
  required,
  variant = 'outlined',
  helperText,
  multiline,
  maxRows,
  placeholder = '',
  startIcon,
  autoComplete,
  rows,
  className,
  autoFocus = false,
  placeholderColor = '#C2C2C2',
  borderWidthFocus = '1px',
  readOnly = false,
  inactive = false,
  onFocus
}) => {
  const isInputDisabled = disabled || readOnly;
  const isInactive = isInputDisabled || inactive;
  const [showPassword, setShowPassword] = useState(false);

  const handleClickShowPassword = () => setShowPassword(show => !show);

  const handleMouseDownPassword = e => {
    e.preventDefault();
  };

  const handleMouseUpPassword = e => {
    e.preventDefault();
  };

  return (
    <React.Fragment>
      <TextField
        placeholder={placeholder}
        className={className}
        label={label}
        required={required}
        variant={variant}
        autoFocus={autoFocus}
        error={error}
        helperText={helperText}
        multiline={multiline}
        maxRows={maxRows}
        value={value}
        onChange={!isInputDisabled ? onChange : null}
        onFocus={onFocus}
        name={name}
        size={size}
        disabled={disabled}
        fullWidth
        autoComplete={autoComplete}
        type={showPassword ? 'text' : type}
        rows={rows ? rows : 1}

        slotProps={{
          input: {
            ...(type === 'password' && {
              endAdornment: (
                <InputAdornment position='end'>
                  <IconButton
                    aria-label={showPassword ? 'hide the password' : 'display the password'}
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                    onMouseUp={handleMouseUpPassword}
                    edge='end'
                    sx={{
                      color: 'black'
                    }}
                  >
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              )
            }),
            ...(startIcon && {
              startAdornment: (
                <InputAdornment position='end' sx={{ opacity: isInactive ? 0.45 : 1 }}>
                  {startIcon}
                </InputAdornment>
              )
            }),
            ...(type === 'phone' && { inputComponent: TextMask }),
          },
          htmlInput: {
            readOnly,
            ...(type === 'phone' && { pattern: '[0-9]*', maskformat: placeholder }),
            ...(type === 'number' && { inputMode: 'numeric' })
          }
        }}
        sx={{
          minWidth: '10rem',
          input: {
            color: 'natural.dark3',
            fontSize: label ? '18px' : '24px',
            fontFamily: label ? 'outfit' : 'Baskervville'
          },
          '& .MuiInputBase-root': {
            paddingLeft: startIcon ? '3px' : '6px',
            backgroundColor: isInactive ? 'neutral.light4' : 'white'
          },
          '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
            display: 'none'
          },
          '& input[type=number]': {
            MozAppearance: 'textfield'
          },
          '& .MuiInputBase-input': {
            color: isInactive ? 'neutral.dark1' : 'black',
            padding: label ? '12px 6px' : '9px 6px',
            cursor: isInputDisabled ? 'pointer' : 'text',
            '&::placeholder': {
              color: isInactive ? 'neutral.dark2' : placeholderColor
            }
          },
          '& label.Mui-focused': {
            color: 'black'
          },
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: isInactive
                ? 'neutral.light1'
                : label
                  ? 'black'
                  : 'neutral.light1',
              borderWidth: '1px',
              borderRadius: '0px',
              color: 'natural.dark3'
            },
            '&:hover fieldset': {
              borderColor: isInactive
                ? 'neutral.light1'
                : label
                  ? 'black'
                  : 'neutral.light1',
              borderWidth: '1px',
              borderRadius: '0px',
              color: isInactive ? 'neutral.dark1' : 'black'
            },
            '&.Mui-focused fieldset': {
              borderColor: isInactive ? 'neutral.light1' : 'black',
              borderWidth: isInactive ? '1px' : borderWidthFocus,
              borderRadius: '0px',
              color: isInactive ? 'neutral.dark1' : 'black'
            }
          }
        }}
      />
    </React.Fragment>
  );
};
